import type { ReactElement, RefObject } from 'react';
import type { MediaPlayerInstance } from '@vidstack/react';
import type {
  BasicContentBlockA,
  BasicContentBlockB,
  CategorizationBlock,
  ClickExpandBlock,
  ComparisonSliderBlock,
  CourseBlock,
  ElectronicSignatureBlock,
  LessonCompleteBlock,
  LikertScaleBlock,
  LinkBlock,
  MapBlock,
  PolicyAcknowledgementBlock,
  StylizedNumberedListBlock,
  StylizedSidebarCalloutBlock,
  StylizedTextCalloutBlock,
  VideoBlock,
  WorkplaceColorSpectrumBlock,
  SingleChoiceAnswerBlock,
  FreeformTextBlock,
} from '@course-authoring/shared-types';
import { questionBlockHasContent } from '@course-authoring/shared-types';
import { QuestionBlockFallback } from './QuestionBlockFallback';

import { BasicContentBlockARenderer } from './BasicContentBlockARenderer';
import { BasicContentBlockBRenderer } from './BasicContentBlockBRenderer';
import { ClickExpandRenderer } from './ClickExpandRenderer';
import { ComparisonSliderRenderer } from './ComparisonSliderRenderer';
import { LikertScaleRenderer, type LikertResultsDistribution } from './LikertScaleRenderer';
import { FreeformTextRenderer } from './FreeformTextRenderer';
import { StylizedNumberedListRenderer } from './StylizedNumberedListRenderer';
import { StylizedSidebarCalloutRenderer } from './StylizedSidebarCalloutRenderer';
import { StylizedTextCalloutRenderer } from './StylizedTextCalloutRenderer';
import { VideoRenderer, type VideoTranscriptOperations } from './VideoRenderer';
import {
  WorkplaceColorSpectrumRenderer,
  type WorkplaceColorSpectrumResultsDistribution,
} from './WorkplaceColorSpectrumRenderer';
import { MapRenderer } from './MapRenderer';
import { PolicyAcknowledgementRenderer } from './PolicyAcknowledgementRenderer';
import { LinkRenderer } from './LinkRenderer';
import { ElectronicSignatureRenderer } from './ElectronicSignatureRenderer';
import { CategorizationRenderer } from './CategorizationRenderer';
import { LessonCompleteRenderer } from './LessonCompleteRenderer';
import { SingleChoiceAnswerRenderer, type SingleChoiceAnswerResultsData } from './SingleChoiceAnswerRenderer';
import type { ClickExpandTargetRegistration, NarrationEventHandler } from './narrationEvents';

/**
 * Real aggregated results (NA-181) — Likert, WCS, and ungated Single Choice.
 * `'below-threshold'` (NA-72) signals a Likert/WCS block whose only
 * available sections were below the account's response threshold — Single
 * Choice instead carries this via its own `communityAvailable`/
 * `orgAvailable` flags, both false. `'pending'` signals the results fetch is
 * in flight - shown briefly right after submit, before real data is known,
 * so the renderer doesn't flash fake placeholder data while waiting.
 */
export type BlockResultsDistributions =
  | readonly LikertResultsDistribution[]
  | readonly WorkplaceColorSpectrumResultsDistribution[]
  | SingleChoiceAnswerResultsData
  | 'below-threshold'
  | 'pending';

interface CourseCanvasBlockRendererParams {
  block: CourseBlock;
  videoRef: RefObject<MediaPlayerInstance | null>;
  onInteractionComplete?: () => void;
  /**
   * Learner answer emission for loggable question blocks (Single Choice,
   * Likert, WCS — not Categorization). Only wired by the export runtime;
   * authoring-hosted canvases leave it undefined (NA-178).
   */
  onAnswerSubmit?: (answer: { optionId: string }) => void;
  /**
   * Freeform Text's answer emission — separately typed from `onAnswerSubmit`
   * (text, not an option id), same pattern as `onCategorizationAnswerSubmit`
   * below. Only wired by the export runtime (NA-444).
   */
  onFreeformTextAnswerSubmit?: (answer: { text: string }) => void;
  /**
   * Real aggregated results for this specific block (NA-181, Likert/WCS
   * only). Undefined falls back to each renderer's own placeholder data.
   */
  resultsDistributions?: BlockResultsDistributions;
  /**
   * Whether the player has found a prior recorded answer for this block on
   * bootstrap (NA-319). `'loading'` means the bootstrap prior-answers lookup
   * hasn't resolved yet — the block should show neither its interactive
   * controls nor a locked view. `'unanswered'` is the default for blocks with
   * no prior answer, or for callers (authoring previews) that don't opt in.
   * Only meaningful for Likert, Single Choice, and WCS.
   */
  priorAnswerStatus?: 'loading' | 'answered' | 'unanswered';
  /** The learner's previously recorded `answerOptionId`, present only when `priorAnswerStatus === 'answered'`. */
  priorAnswerOptionId?: string;
  /**
   * NA-319 Categorization relaunch restore. Categorization has no persisted
   * per-learner response, but it can only be marked complete once every item
   * is placed correctly — so if this block's own completion tracking says
   * it's done, the correct assignment (item id -> its own `correctCategoryId`)
   * is guaranteed accurate. Derived entirely from the block's own content in
   * `CourseCanvas.tsx`, never fetched. Undefined leaves the block interactive.
   *
   * NOT currently populated from completion-status tracking (see NA-319
   * design.md decision 3b — that approach was found unsafe and reverted).
   * `CourseCanvas.tsx` supplies this once it has verified a restored
   * `categorizationFingerprintByBlockId` entry still matches this block's
   * current correct-category data (see `contentFingerprint.ts`), or from the
   * learner's own real, actually-submitted assignments earlier in the same
   * browser session — never inferred, so a restore can never fabricate a
   * completion that didn't happen.
   */
  categorizationInitialCorrectAssignments?: Record<string, string>;
  /**
   * NA-319: fires with the learner's real, actually-submitted Categorization
   * assignments the moment the block genuinely completes (every item
   * correct). Only wired by the export runtime, same as `onAnswerSubmit` for
   * the other question types — authoring-hosted canvases leave it undefined.
   */
  onCategorizationAnswerSubmit?: (assignments: Record<string, string>) => void;
  /**
   * NA-319 Electronic Signature / Policy Acknowledgement relaunch restore,
   * reused as-is by Link (NA-409): none of the three block types have any
   * backend persistence (no `questionDefinitionId`, no save path), so this
   * uses the same two-tier restore as Categorization: real same-session data
   * first (below), falling back to a lesson-completion-derived signal only
   * when this specific block instance's own `requireInteraction` gating
   * means the lesson could not have completed without the checkbox being
   * checked (or, for Link, the link being clicked). Derived entirely in
   * `CourseCanvas.tsx`.
   */
  acknowledgementInitialAcknowledged?: boolean;
  /**
   * NA-319: fires the instant the learner actually checks an Electronic
   * Signature or Policy Acknowledgement checkbox, or (NA-409) clicks a Link
   * block's link — the real fact, never inferred. Only wired by the export
   * runtime.
   */
  onAcknowledgementSubmit?: () => void;
  isStandalone?: boolean;
  onLessonCompleteContinue?: () => void;
  lessonThemeColor?: string;
  onLessonCompleteSaveExit?: () => void;
  /** Content locale currently being viewed; forwarded to the video renderer. */
  currentLocale?: string;
  transcriptOperations?: VideoTranscriptOperations;
  onNarrationEvent?: NarrationEventHandler;
  /** Click-Expand narration target for this block, keyed by block id (see ClickExpandRenderer). */
  onClickExpandNarrationTargetChange?: ClickExpandTargetRegistration;
}

type CourseCanvasBlockRenderer = (
  params: CourseCanvasBlockRendererParams
) => ReactElement | null;

type QuestionContentBlock =
  | LikertScaleBlock
  | SingleChoiceAnswerBlock
  | WorkplaceColorSpectrumBlock
  | CategorizationBlock
  | FreeformTextBlock;

/**
 * Renders a question block's renderer when it has selected content, else the
 * title-only, non-blocking fallback. Shared by the four question block entries.
 */
function withQuestionFallback(
  block: QuestionContentBlock,
  onInteractionComplete: (() => void) | undefined,
  renderer: ReactElement,
): ReactElement {
  return questionBlockHasContent(block)
    ? renderer
    : (
      <QuestionBlockFallback
        title={block.title}
        titleLevel={block.titleLevel}
        onInteractionComplete={onInteractionComplete}
      />
    );
}

const courseCanvasBlockRendererRegistry: Partial<
  Record<CourseBlock['type'], CourseCanvasBlockRenderer>
> = {
  video: ({
    block,
    videoRef,
    onInteractionComplete,
    lessonThemeColor,
    currentLocale,
    transcriptOperations,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) => {
    const videoBlock = block as VideoBlock;

    return (
      <VideoRenderer
        ref={videoRef}
        url={videoBlock.url}
        posterUrl={videoBlock.posterUrl}
        tracks={videoBlock.tracks}
        title={videoBlock.title}
        shortDescription={videoBlock.shortDescription}
        longDescription={videoBlock.longDescription}
        requireInteraction={videoBlock.requireInteraction}
        interactionMessage={videoBlock.interactionMessage}
        onInteractionComplete={onInteractionComplete}
        lessonThemeColor={lessonThemeColor}
        currentLocale={currentLocale}
        centerstageVideoId={videoBlock.centerstageVideoId}
        transcriptOperations={transcriptOperations}
        onPlaybackStarted={() => onNarrationEvent?.({
          type: 'video-playback-started',
          blockId: videoBlock.id,
        })}
        onPlaybackCompleted={() => onNarrationEvent?.({
          type: 'video-playback-completed',
          blockId: videoBlock.id,
        })}
      />
    );
  },
  'basic-content-a': ({ block }: CourseCanvasBlockRendererParams) => (
    <BasicContentBlockARenderer block={block as BasicContentBlockA} />
  ),
  'basic-content-b': ({ block }: CourseCanvasBlockRendererParams) => (
    <BasicContentBlockBRenderer block={block as BasicContentBlockB} />
  ),
  'stylized-text-callout': ({ block }: CourseCanvasBlockRendererParams) => {
    const calloutBlock = block as StylizedTextCalloutBlock;
    return (
      <StylizedTextCalloutRenderer
        content={calloutBlock.content}
        textSize={calloutBlock.textSize}
      />
    );
  },
  'stylized-numbered-list': ({ block, lessonThemeColor }: CourseCanvasBlockRendererParams) => {
    const listBlock = block as StylizedNumberedListBlock;

    return (
      <StylizedNumberedListRenderer
        title={listBlock.title}
        titleLevel={listBlock.titleLevel}
        items={listBlock.items}
        listStyle={listBlock.listStyle}
        lessonThemeColor={lessonThemeColor}
      />
    );
  },
  'stylized-sidebar-callout': ({ block }: CourseCanvasBlockRendererParams) => {
    const sidebarBlock = block as StylizedSidebarCalloutBlock;

    return (
      <StylizedSidebarCalloutRenderer
        title={sidebarBlock.title}
        titleLevel={sidebarBlock.titleLevel}
        body={sidebarBlock.body}
        calloutTitle={sidebarBlock.calloutTitle}
        calloutBody={sidebarBlock.calloutBody}
      />
    );
  },
  'comparison-slider': ({
    block,
    onInteractionComplete,
    lessonThemeColor,
    currentLocale,
    transcriptOperations,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) => (
    <ComparisonSliderRenderer
      block={block as ComparisonSliderBlock}
      onInteractionComplete={onInteractionComplete}
      lessonThemeColor={lessonThemeColor}
      currentLocale={currentLocale}
      transcriptOperations={transcriptOperations}
      onSideSelected={(side) => onNarrationEvent?.({
        type: 'comparison-side-selected',
        blockId: block.id,
        side,
      })}
      onSideVideoPlaybackStarted={(side) => onNarrationEvent?.({
        type: 'comparison-video-playback-started',
        blockId: block.id,
        side,
      })}
      onSideVideoPlaybackCompleted={(side) => onNarrationEvent?.({
        type: 'comparison-video-playback-completed',
        blockId: block.id,
        side,
      })}
    />
  ),
  'click-expand': ({
    block,
    onInteractionComplete,
    onNarrationEvent,
    onClickExpandNarrationTargetChange,
  }: CourseCanvasBlockRendererParams) => (
    <ClickExpandRenderer
      block={block as ClickExpandBlock}
      onInteractionComplete={onInteractionComplete}
      onTopicSelected={(topicId, topicIndex) => onNarrationEvent?.({
        type: 'topic-selected',
        blockId: block.id,
        topicId,
        topicIndex,
      })}
      onTopicClosed={(topicId, topicIndex) => onNarrationEvent?.({
        type: 'topic-closed',
        blockId: block.id,
        topicId,
        topicIndex,
      })}
      onNarrationTargetChange={(target, previousTarget) => onClickExpandNarrationTargetChange?.(
        block.id,
        target,
        previousTarget,
      )}
    />
  ),
  'workplace-color-spectrum': ({
    block,
    onInteractionComplete,
    onAnswerSubmit,
    resultsDistributions,
    priorAnswerStatus,
    priorAnswerOptionId,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) =>
    withQuestionFallback(
      block as WorkplaceColorSpectrumBlock,
      onInteractionComplete,
      <WorkplaceColorSpectrumRenderer
        block={block as WorkplaceColorSpectrumBlock}
        onInteractionComplete={onInteractionComplete}
        onAnswerSubmit={onAnswerSubmit}
        resultsDistributions={resultsDistributions as readonly WorkplaceColorSpectrumResultsDistribution[] | 'below-threshold' | 'pending' | undefined}
        priorAnswerStatus={priorAnswerStatus}
        priorAnswerOptionId={priorAnswerOptionId}
        onNarrationOutcomeCommitted={(outcome, presentation) => onNarrationEvent?.({
          type: 'question-outcome-committed',
          blockId: block.id,
          outcome,
          presentation,
        })}
      />,
    ),
  'likert-scale': ({
    block,
    onInteractionComplete,
    onAnswerSubmit,
    resultsDistributions,
    priorAnswerStatus,
    priorAnswerOptionId,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) =>
    withQuestionFallback(
      block as LikertScaleBlock,
      onInteractionComplete,
      <LikertScaleRenderer
        block={block as LikertScaleBlock}
        onInteractionComplete={onInteractionComplete}
        onAnswerSubmit={onAnswerSubmit}
        resultsDistributions={resultsDistributions as readonly LikertResultsDistribution[] | 'below-threshold' | 'pending' | undefined}
        priorAnswerStatus={priorAnswerStatus}
        priorAnswerOptionId={priorAnswerOptionId}
        onNarrationOutcomeCommitted={(outcome, presentation) => onNarrationEvent?.({
          type: 'question-outcome-committed',
          blockId: block.id,
          outcome,
          presentation,
        })}
      />,
    ),
  map: ({
    block,
    onInteractionComplete,
    lessonThemeColor,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) => (
    <MapRenderer
      block={block as MapBlock}
      onInteractionComplete={onInteractionComplete}
      regionColor={lessonThemeColor}
      onRegionOpened={(regionId) => onNarrationEvent?.({
        type: 'map-region-opened',
        blockId: block.id,
        regionId,
      })}
      onRegionChanged={(previousRegionId, regionId) => onNarrationEvent?.({
        type: 'map-region-changed',
        blockId: block.id,
        previousRegionId,
        regionId,
      })}
      onRegionClosed={(regionId) => onNarrationEvent?.({
        type: 'map-region-closed',
        blockId: block.id,
        regionId,
      })}
    />
  ),
  'policy-acknowledgement': ({
    block,
    onInteractionComplete,
    onNarrationEvent,
    acknowledgementInitialAcknowledged,
    onAcknowledgementSubmit,
  }: CourseCanvasBlockRendererParams) => (
    <PolicyAcknowledgementRenderer
      block={block as PolicyAcknowledgementBlock}
      onInteractionComplete={onInteractionComplete}
      initialAcknowledged={acknowledgementInitialAcknowledged}
      onAcknowledged={() => {
        onAcknowledgementSubmit?.();
        onNarrationEvent?.({
          type: 'acknowledged',
          blockId: block.id,
          acknowledgement: 'document',
        });
      }}
    />
  ),
  link: ({
    block,
    onInteractionComplete,
    onNarrationEvent,
    acknowledgementInitialAcknowledged,
    onAcknowledgementSubmit,
  }: CourseCanvasBlockRendererParams) => (
    <LinkRenderer
      block={block as LinkBlock}
      onInteractionComplete={onInteractionComplete}
      initialLinkOpened={acknowledgementInitialAcknowledged}
      onLinkOpened={() => {
        onAcknowledgementSubmit?.();
        onNarrationEvent?.({
          type: 'acknowledged',
          blockId: block.id,
          acknowledgement: 'link',
        });
      }}
    />
  ),
  'electronic-signature': ({
    block,
    onInteractionComplete,
    onNarrationEvent,
    acknowledgementInitialAcknowledged,
    onAcknowledgementSubmit,
  }: CourseCanvasBlockRendererParams) => (
    <ElectronicSignatureRenderer
      block={block as ElectronicSignatureBlock}
      onInteractionComplete={onInteractionComplete}
      initialAcknowledged={acknowledgementInitialAcknowledged}
      onAcknowledged={() => {
        onAcknowledgementSubmit?.();
        onNarrationEvent?.({
          type: 'acknowledged',
          blockId: block.id,
          acknowledgement: 'electronic-signature',
        });
      }}
    />
  ),
  categorization: ({
    block,
    onInteractionComplete,
    categorizationInitialCorrectAssignments,
    onCategorizationAnswerSubmit,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) =>
    withQuestionFallback(
      block as CategorizationBlock,
      onInteractionComplete,
      <CategorizationRenderer
        block={block as CategorizationBlock}
        onInteractionComplete={onInteractionComplete}
        initialCorrectAssignments={categorizationInitialCorrectAssignments}
        onAssignmentsSubmitted={onCategorizationAnswerSubmit}
        onNarrationOutcomeCommitted={(outcome, presentation) => onNarrationEvent?.({
          type: 'question-outcome-committed',
          blockId: block.id,
          outcome,
          presentation,
        })}
      />,
    ),
  'single-choice-answer': ({
    block,
    onInteractionComplete,
    onAnswerSubmit,
    resultsDistributions,
    priorAnswerStatus,
    priorAnswerOptionId,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) =>
    withQuestionFallback(
      block as SingleChoiceAnswerBlock,
      onInteractionComplete,
      <SingleChoiceAnswerRenderer
        block={block as SingleChoiceAnswerBlock}
        onInteractionComplete={onInteractionComplete}
        onAnswerSubmit={onAnswerSubmit}
        resultsData={resultsDistributions as SingleChoiceAnswerResultsData | 'pending' | undefined}
        priorAnswerStatus={priorAnswerStatus}
        priorAnswerOptionId={priorAnswerOptionId}
        onNarrationOutcomeCommitted={(outcome, presentation) => onNarrationEvent?.({
          type: 'question-outcome-committed',
          blockId: block.id,
          outcome,
          presentation,
        })}
      />,
    ),
  'freeform-text': ({
    block,
    onInteractionComplete,
    onFreeformTextAnswerSubmit,
    priorAnswerStatus,
    onNarrationEvent,
  }: CourseCanvasBlockRendererParams) =>
    withQuestionFallback(
      block as FreeformTextBlock,
      onInteractionComplete,
      <FreeformTextRenderer
        block={block as FreeformTextBlock}
        onInteractionComplete={onInteractionComplete}
        onAnswerSubmit={onFreeformTextAnswerSubmit}
        priorAnswerStatus={priorAnswerStatus}
        onNarrationOutcomeCommitted={(outcome, presentation) => onNarrationEvent?.({
          type: 'question-outcome-committed',
          blockId: block.id,
          outcome,
          presentation,
        })}
      />,
    ),
  'lesson-complete': ({
    block,
    isStandalone,
    onLessonCompleteContinue,
    onLessonCompleteSaveExit,
  }: CourseCanvasBlockRendererParams) => (
    <LessonCompleteRenderer
      block={block as LessonCompleteBlock}
      isStandalone={isStandalone}
      onContinue={onLessonCompleteContinue}
      onSaveExit={onLessonCompleteSaveExit}
    />
  ),
};

export function renderCourseCanvasBlock(
  params: CourseCanvasBlockRendererParams
): ReactElement | null {
  const renderer = courseCanvasBlockRendererRegistry[params.block.type];
  return renderer ? renderer(params) : null;
}

/** Registry-completeness check (TOOL-5 test support). */
export function hasCourseCanvasBlockRenderer(type: CourseBlock['type']): boolean {
  return courseCanvasBlockRendererRegistry[type] !== undefined;
}
